import { css } from '@emotion/react';
import {
  type FC, useCallback, useEffect, useRef, useState,
} from 'react';
import { useResizeObserver } from '~/_shared/hooks/useResizeObserver';
import { useDebounce } from '~/_shared/utils/hooks/useDebounce';
import { type StreetViewState } from '~/store/frontendState/mapTools/streetView/streetView.state';
import { type MapSettingsPublicMapSettingsState } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.state';
import { RightSideMyLocationMenuItem } from './MenuItems/myLocation/rightSideMyLocationMenuItem.container';
import { RightSideAddIndividualRadiusItem } from './MenuItems/rightSideAddIndividualRadiusMenuItem.container';
import { RightSideAddMarkerMenuItem } from './MenuItems/rightSideAddMarkerMenuItem.container';
import { RightSideCloseStreetViewMenuItemContainer } from './MenuItems/rightSideCloseStreetViewMenuItem.container';
import { RightSideExportDataViewMenuItem } from './MenuItems/rightSideExportDataMenuItem.container';
import { RightSideExportImageMenuItem } from './MenuItems/rightSideExportImageMenuItem.container';
import { RightSideLassoToolMenuItem } from './MenuItems/rightSideLassoToolMenuItem.container';
import { RightSideLocationFinderMenuItem } from './MenuItems/rightSideLocationFinderMenuItem.container';
import { RightSideMapDistanceCalculatorMenuItem } from './MenuItems/rightSideMapDistanceCalculatorMenuItem.container';
import { RightSideMapKeyMenuItemContainer } from './MenuItems/rightSideMapKeyMenuItem.container';
import { RightSideMapSearchMenuItem } from './MenuItems/rightSideMapSearchMenuItem.container';
import { RightSideSatelliteViewMenuItem } from './MenuItems/rightSideSatelliteViewMenuItem.container';

const PEGMAN_SAFE_OFFSET_PX = 68;
const MIN_REQUIRED_HEIGHT = 40;
const BUTTON_HEIGHT_PX = 36;
const COLLAPSE_DEBOUNCE_MS = 80;

const rightSideMapToolsStyle = (hide: boolean) => css({
  columnGap: '2px',
  columns: '32px',
  direction: 'rtl',
  maxHeight: `calc(100% - ${PEGMAN_SAFE_OFFSET_PX}px)`,
  minHeight: BUTTON_HEIGHT_PX,
  position: 'absolute',
  right: 5,
  top: 5,
  visibility: hide ? 'hidden' : 'visible',
  zIndex: 1,
});

type RightSideMapToolsProps = Readonly<{
  forceHideLasso?: boolean;
  isMapPresentational: boolean;
  isEditModeDisabled: boolean;
  isExportDataEnabled: boolean;
  isLayeredMapConnected: boolean;
  isMobileScreen: boolean;
  publicMapSettings?: MapSettingsPublicMapSettingsState;
  streetView: StreetViewState;
}>;

export const RightSideMapToolsComponent: FC<RightSideMapToolsProps> = ({
  forceHideLasso,
  isEditModeDisabled,
  isExportDataEnabled,
  isMapPresentational,
  isLayeredMapConnected,
  isMobileScreen,
  publicMapSettings,
  streetView,
}: RightSideMapToolsProps) => {
  const isMapNotPresentationalOr = useCallback((other: boolean | undefined | null) => (!isMapPresentational || other), [isMapPresentational]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const wrapperSize = useResizeObserver<HTMLDivElement>({ ref: wrapperRef, box: 'content-box' });

  const [collapsed, setCollapsed] = useState(false);

  const availableHeight = wrapperSize.height ?? 0;

  const recompute = useCallback(() => {
    if (wrapperRef.current && innerRef.current) {
      const wrapperHeight = wrapperRef.current.offsetHeight;
      const innerHeight = innerRef.current.scrollHeight;

      // The inner div doesn't have columns, so its scrollHeight represents
      // the total height needed if all items were in a single column
      // If innerHeight > wrapperHeight, content has wrapped into multiple columns
      const isConstrainedByMaxHeight = innerHeight > wrapperHeight;

      const headroom = availableHeight - MIN_REQUIRED_HEIGHT;

      setCollapsed(headroom < 0 && isConstrainedByMaxHeight);
    }
  }, [availableHeight]);

  const recomputeDebounced = useDebounce(recompute, COLLAPSE_DEBOUNCE_MS);

  useEffect(() => {
    if (availableHeight) {
      recomputeDebounced();
    }
  }, [availableHeight, recomputeDebounced]);

  if (streetView.active) {
    return (
      <div css={rightSideMapToolsStyle(false)}>
        <RightSideCloseStreetViewMenuItemContainer />
      </div>
    );
  }

  if (isMobileScreen) {
    return null;
  }

  return (
    <div css={rightSideMapToolsStyle(collapsed)} ref={wrapperRef}>
      <div ref={innerRef}>
        {isMapNotPresentationalOr(publicMapSettings?.mapKey) && <RightSideMapKeyMenuItemContainer />}
        {isMapNotPresentationalOr(publicMapSettings?.displaySearchBox && !publicMapSettings?.searchBarAlwaysVisible) &&
          <RightSideMapSearchMenuItem />}
        {isMapNotPresentationalOr(publicMapSettings?.allowSatelliteView) && <RightSideSatelliteViewMenuItem />}
        {isMapNotPresentationalOr(publicMapSettings?.showMyLocation) && <RightSideMyLocationMenuItem />}
        {!isMapPresentational && <RightSideExportImageMenuItem />}
        {isMapNotPresentationalOr(publicMapSettings?.distanceCalculator) && <RightSideMapDistanceCalculatorMenuItem />}
        {isMapNotPresentationalOr(publicMapSettings?.locationFinder) && <RightSideLocationFinderMenuItem />}
        {!forceHideLasso && isMapNotPresentationalOr(publicMapSettings?.lassoTool) && <RightSideLassoToolMenuItem />}
        {!isEditModeDisabled && <RightSideAddIndividualRadiusItem />}
        {!isEditModeDisabled && !isLayeredMapConnected && <RightSideAddMarkerMenuItem />}
        {isExportDataEnabled && <RightSideExportDataViewMenuItem />}
      </div>
    </div>
  );
};
